import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ConfigProvider,
  theme,
  Button,
  Typography,
  Row,
  Col,
  Card,
} from "antd";
import "./LandingPreview.scss";

import heroSlip from "./images/hero-slip-handover.jpg";
import happyMerchant from "./images/happy-merchant-phone.jpg";
import pushNotification from "./images/app-notification-mockup.png";
import tallyAuditReceipts from "./images/tally-slips-audit.png";
import merchantAvatar from "./images/merchant-avatar-gold-black.png";
import customerAvatar from "./images/customer-avatar.jpg";
import tallyIcon from "./images/tally-relationship-icon1.png";

const { Title, Text } = Typography;

const useScrollReveal = () => {
  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("revealed"); observer.unobserve(e.target); } }); },
      { threshold: 0.05 }
    );
    document.querySelectorAll("[data-reveal]").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

const useCountUp = (target: string, duration = 1200) => {
  const [display, setDisplay] = useState("0");
  const ref = useRef<HTMLDivElement>(null);
  const animated = useRef(false);
  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const numericTarget = parseFloat(target.replace(/[^0-9.]/g, ""));
    if (isNaN(numericTarget) || prefersReduced) { setDisplay(target); return; }
    const suffix = target.replace(/[0-9.]/g, "");
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !animated.current) {
        animated.current = true;
        const start = performance.now();
        const tick = (now: number) => {
          const p = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          const cur = Math.round(eased * numericTarget * 10) / 10;
          setDisplay(`${cur % 1 === 0 ? Math.round(cur) : cur}${suffix}`);
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        observer.disconnect();
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);
  return { display, ref };
};

const StatNumber: React.FC<{ value: string }> = ({ value }) => {
  const { display, ref } = useCountUp(value);
  return <div className="why-tally-number" ref={ref}>{display}</div>;
};

const palette = {
  orange: "#FF6B01", dark: "#0F0F0F", white: "#FFFFFF",
  border: "#F1F1F1", secondary: "rgba(15,15,15,0.45)",
};

const MobileNav: React.FC<{ tallyIcon: string }> = ({ tallyIcon }) => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  return (
    <>
      <header className="tally-header">
        <div className="header-container">
          <div className="brand">
            <img src={tallyIcon} alt="Tally" className="brand-icon" />
            <span className="brand-name">Tally</span>
          </div>
          <div className="nav-links desktop-nav">
            <a href="#how-it-works" className="nav-link-anchor">How it works</a>
            <a href="#pricing-plans" className="nav-link-anchor">Pricing</a>
            <a href="mailto:hello@gettally.store" className="nav-link-anchor">Contact</a>
            <a href="https://app.gettally.store" className="nav-cta-anchor">Login</a>
          </div>
          <button className={`hamburger ${open ? "hamburger--open" : ""}`} onClick={() => setOpen(!open)} aria-label="Menu">
            <span className="hamburger__bar" /><span className="hamburger__bar" /><span className="hamburger__bar" />
          </button>
        </div>
      </header>
      <div className={`mobile-drawer ${open ? "mobile-drawer--open" : ""}`} aria-hidden={!open}>
        <nav className="mobile-drawer__nav">
          <a href="#how-it-works" className="mobile-drawer__link" onClick={close}>How it works</a>
          <a href="#pricing-plans" className="mobile-drawer__link" onClick={close}>Pricing</a>
          <a href="mailto:hello@gettally.store" className="mobile-drawer__link" onClick={close}>Contact</a>
          <div className="mobile-drawer__cta">
            <a href="https://app.gettally.store" className="mobile-drawer__login-link" onClick={close}>
              <Button type="primary" size="large" className="mobile-drawer__login">Login</Button>
            </a>
          </div>
        </nav>
      </div>
      {open && <div className="mobile-drawer__backdrop" onClick={close} />}
    </>
  );
};

const LandingPreview: React.FC = () => {
  useScrollReveal();
  const industries = ["Pharmacies","Barbershops","Restaurants","Minimarts","Boutiques","Cosmetics Shops","Logistics Hubs","Gas Stations","Guest Houses","Juice Bars","Fashion Stores","Provisions"];

  return (
    <ConfigProvider
      theme={{
        algorithm: theme.defaultAlgorithm,
        token: { colorPrimary: palette.orange, colorBgBase: palette.white, colorTextBase: palette.dark, colorTextSecondary: palette.secondary, colorBorder: palette.border, borderRadius: 8 },
        components: {
          Button: { borderRadius: 8, controlHeight: 56, fontWeight: 600, colorPrimary: palette.dark, colorPrimaryHover: "#1A1A1A", colorPrimaryActive: "#000000" },
          Card: { borderRadius: 8 },
        },
      }}
    >
      <MobileNav tallyIcon={tallyIcon} />

      <div className="tally-landing">

        {/* ── HERO ────────────────────────────────────────────────────── */}
        <section className="hero">
          <div className="hero-content" data-reveal data-delay="0">
            <span className="hero-eyebrow">Built for Nigerian shops</span>

            <Title level={1} className="headline">
              Tally helps you bring your customers back and sell more.
            </Title>

            <div className="sub-head">
              When a customer stops coming, most shop owners never know.
              Tally tells you — and helps you bring them back before they're gone for good.
            </div>

            <div className="hero-ctas">
              <Button type="primary" size="large" className="cta-app">
                Start Free — 60 Days, No Card Needed
              </Button>
              <Button type="default" size="large" className="cta-business cta-ghost">
                See How It Works →
              </Button>
            </div>

            <div className="hero-social-proof">
              <span className="hero-stars">★★★★★</span>
              <span className="hero-proof-text">
                Trusted by shops across Lagos, Abuja, Port Harcourt and beyond
              </span>
            </div>

            <div className="hero-objection-killers">
              <span className="objection-item">✓ No app download needed</span>
              <span className="objection-item">✓ Zero staff training</span>
              <span className="objection-item">✓ Free for 60 days</span>
            </div>
          </div>

          <div className="hero-image-wrapper" data-reveal data-delay="200">
            <img src={heroSlip} alt="Staff handing Tally slip to customer" className="hero-image" />
          </div>
        </section>

        {/* ── TRUST RIBBON ────────────────────────────────────────────── */}
        <div className="trust-ribbon">
          <div className="trust-content">
            <Text className="trust-headline">Already working for shops like yours across Nigeria</Text>
            <div className="industry-carousel">
              <div className="carousel-track">
                {industries.map((item, i) => <span key={i} className="carousel-item">• {item}</span>)}
                {industries.map((item, i) => <span key={`d${i}`} className="carousel-item">• {item}</span>)}
              </div>
            </div>
          </div>
        </div>

        {/* ── HOW IT WORKS ────────────────────────────────────────────── */}
        <section className="chain-section" id="how-it-works">
          <div className="chain-wrapper">
            <div className="chain-left">
              <div className="chain-container">

                <div className="chain-step" data-reveal data-delay="0">
                  <h3>1. Customer pays — you give them a slip code</h3>
                  <p>
                    Someone buys from you. You open Tally, enter the amount,
                    and get a unique code. Give it to them on paper, a receipt,
                    or show it on your screen. Takes seconds.
                  </p>
                </div>

                <div className="chain-step" data-reveal data-delay="150">
                  <h3>2. They enter the code — their visit is saved</h3>
                  <p>
                    Customer opens go.gettally.store on their phone and enters
                    the code. Their name and visit are saved to your account
                    instantly. No download needed. No app store.
                  </p>
                </div>

                <div className="chain-step" data-reveal data-delay="300">
                  <h3>3. They earn a reward — so they come back</h3>
                  <p>
                    Every visit moves them closer to a free reward you set —
                    like a free service or discount after 5 visits. They
                    can see their progress. That progress makes them want
                    to come back and complete it.
                  </p>
                </div>

                <div className="chain-step" data-reveal data-delay="450">
                  <h3>4. Tally tells you when they stop coming</h3>
                  <p>
                    If a customer who usually comes every 2 weeks has not
                    shown up in 5 weeks, Tally tells you. You send them one
                    message. They come back. That is the whole idea.
                  </p>
                </div>

              </div>
            </div>
            <div className="chain-right" data-reveal data-delay="100">
              <img src={happyMerchant} alt="Shop owner using Tally on phone" className="chain-image" />
            </div>
          </div>
        </section>

        {/* ── COMPARISON ──────────────────────────────────────────────── */}
        <section className="comparison-section">
          <Title level={2} className="section-title">
            Without Tally vs With Tally
          </Title>
          <p className="comparison-subtitle">
            The difference starts from your very next sale.
          </p>

          <div className="comparison-table">
            <div className="comparison-header">
              <div className="comparison-col-head bad">Without Tally</div>
              <div className="comparison-col-head good">With Tally</div>
            </div>
            {[
              {
                bad: "A customer buys and leaves. You have no way to contact them ever again.",
                good: "Every customer who buys is saved. You can message any of them at any time — for free.",
              },
              {
                bad: "A good customer stops coming. You never notice until they are long gone.",
                good: "Tally notices the moment a customer goes quiet. You find out in time to bring them back.",
              },
              {
                bad: "You want to send a promotion but you have no way to reach your customers.",
                good: "Send a message to every customer who ever bought from you. Free. One tap. Any time.",
              },
              {
                bad: "Customers have no reason to choose your shop over the one down the road.",
                good: "Every visit earns them a reward at your shop. That reward keeps pulling them back to you.",
              },
              {
                bad: "You don't know which customers spend the most or come back the most.",
                good: "See every customer — how many times they came, how much they spent, when they last visited.",
              },
              {
                bad: "You suspect your staff are hiding sales but you can't prove it.",
                good: "Every slip is tied to the staff member who issued it. You see exactly where sales go missing.",
              },
            ].map((row, i) => (
              <div className="comparison-row" key={i}>
                <div className="comparison-cell bad-cell">✗ {row.bad}</div>
                <div className="comparison-cell good-cell">✓ {row.good}</div>
              </div>
            ))}
          </div>

          <div className="comparison-cta-wrapper">
            <Button type="primary" size="large" className="comparison-cta-btn">
              Start Free — Bring Your Customers Back Today
            </Button>
          </div>
        </section>

        {/* ── TWO SIDES ───────────────────────────────────────────────── */}
        <section className="dual-section">
          <Title level={2} className="section-title">
            One Tool. Two People Win.
          </Title>

          <Row gutter={[32, 32]} align="stretch">
            <Col xs={24} md={12}>
              <Card className="dual-card customer" data-reveal data-delay="0">
                <div className="card-header">
                  <img src={customerAvatar} alt="Customer" className="avatar" />
                  <h4>If You Shop Here</h4>
                </div>
                <ul>
                  <li>
                    <strong>Every visit counts.</strong> Each time you buy,
                    you get a code. Enter it and your visit is saved. The more
                    you visit, the closer you get to a free reward.
                  </li>
                  <li>
                    <strong>Earn rewards at every Tally shop.</strong> The shop
                    owner sets the reward — free service, discount, or gift.
                    You can see your progress any time on your phone.
                  </li>
                  <li>
                    <strong>Hear from shops you already buy from.</strong>{" "}
                    When a shop you visit has a deal or wants you back, they
                    send you a message directly. Not a random group. Just them,
                    reaching you because you are their customer.
                  </li>
                </ul>
                <img src={pushNotification} alt="Customer reward progress" className="dual-image" style={{ objectPosition: "top center" }} />
              </Card>
            </Col>

            <Col xs={24} md={12}>
              <Card className="dual-card merchant" data-reveal data-delay="150">
                <div className="card-header">
                  <img src={merchantAvatar} alt="Merchant" className="avatar" />
                  <h4>If You Own the Shop</h4>
                </div>
                <ul>
                  <li>
                    <strong>Know every customer by name.</strong> Every time
                    someone redeems a code, their name and details are saved
                    to your account. You finally know who your customers are.
                  </li>
                  <li>
                    <strong>Know who stopped coming — before they're gone.</strong>{" "}
                    Tally watches every customer. When someone who usually
                    comes regularly goes quiet, you are told. You reach out.
                    They come back.
                  </li>
                  <li>
                    <strong>Message all your customers for free.</strong>{" "}
                    Send a promo, a reminder, or a win-back message to every
                    customer you have — in one tap. It costs nothing. Ever.
                  </li>
                  <li>
                    <strong>Give them a reason to keep coming back.</strong>{" "}
                    Set a reward. Tally tracks every visit automatically.
                    When a customer is close to their reward, they feel
                    the pull to come in and complete it.
                  </li>
                  <li>
                    <strong>See if staff are hiding sales.</strong>{" "}
                    Every slip is tied to the staff member who made it.
                    If a slip was issued but never redeemed, you see it —
                    and you see exactly who issued it.
                  </li>
                </ul>
                <img src={tallyAuditReceipts} alt="Tally slip codes" className="dual-image" />
              </Card>
            </Col>
          </Row>

          <div className="inline-cta-wrapper">
            <p className="inline-cta-label">
              Your customers came once. Tally makes sure they come back again and again.
            </p>
            <Button type="primary" size="large" className="inline-cta-btn">
              Start Your 60-Day Free Trial — No Card Needed
            </Button>
          </div>
        </section>

        {/* ── STATS ───────────────────────────────────────────────────── */}
        <section className="why-tally-section">
          <Title level={2} className="section-title">
            What Tally Does for Your Shop
          </Title>

          <Row gutter={[32, 32]}>
            <Col xs={24} sm={12} md={6}>
              <div className="why-tally-card" data-reveal data-delay="0">
                <StatNumber value="5x" />
                <h4>Cheaper to bring back an old customer than find a new one</h4>
                <p>
                  The people who already bought from you are your easiest sale.
                  Tally helps you reach them and bring them back — for free.
                </p>
              </div>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div className="why-tally-card" data-reveal data-delay="150">
                <StatNumber value="₦0" />
                <h4>Cost to send a message to all your customers</h4>
                <p>
                  No SMS bills. No WhatsApp fees. Type one message and send
                  it to everyone who ever bought from you. Free. Every time.
                </p>
              </div>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div className="why-tally-card" data-reveal data-delay="300">
                <StatNumber value="0" />
                <h4>Customers who leave without you knowing — with Tally</h4>
                <p>
                  The moment a regular customer goes quiet, you find out.
                  Not weeks later. Right now. While you can still bring
                  them back.
                </p>
              </div>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div className="why-tally-card" data-reveal data-delay="450">
                <StatNumber value="More" />
                <h4>Sales from customers who keep coming back to you</h4>
                <p>
                  Customers who return spend more and cost you nothing extra.
                  Tally turns one-time buyers into regulars — automatically.
                </p>
              </div>
            </Col>
          </Row>

          <div className="why-tally-cta-wrapper">
            <Button type="primary" size="large" className="why-tally-cta-btn">
              Start Bringing Customers Back — It's Free →
            </Button>
          </div>
        </section>

        {/* ── TESTIMONIALS ────────────────────────────────────────────── */}
        <section className="testimonials-section">
          <Title level={2} className="section-title">
            What Shop Owners Say
          </Title>

          <div className="testimonials-grid">
            <div className="testimonial-card" data-reveal data-delay="0">
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-quote">
                "Before Tally, customers would come once and I'd never see
                them again. Now I know who hasn't come back, I send them a
                message, and they return. My repeat sales went up in the
                first month."
              </p>
              <div className="testimonial-author">
                <div className="testimonial-avatar">AO</div>
                <div>
                  <div className="testimonial-name">Adaeze Okonkwo</div>
                  <div className="testimonial-shop">Glam Touch Beauty, Port Harcourt</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card" data-reveal data-delay="150">
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-quote">
                "I send a message to my customers every Friday. My weekend
                sales go up every time. I used to pay for SMS to do the same
                thing. With Tally it costs nothing."
              </p>
              <div className="testimonial-author">
                <div className="testimonial-avatar">EI</div>
                <div>
                  <div className="testimonial-name">Emeka Ihejirika</div>
                  <div className="testimonial-shop">Sunrise Pharmacy, Lagos</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card" data-reveal data-delay="300">
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-quote">
                "Tally told me three of my regulars had not come back in over
                a month. I messaged them. Two came in that same week.
                Without Tally, I would never have known they were gone."
              </p>
              <div className="testimonial-author">
                <div className="testimonial-avatar">FK</div>
                <div>
                  <div className="testimonial-name">Fatima Kabir</div>
                  <div className="testimonial-shop">The Grill House, Abuja</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── WORKFLOW ────────────────────────────────────────────────── */}
        <section className="workflow-section" id="pricing">
          <Title level={2} className="section-title">
            4 Steps to a Business That Knows Its Customers
          </Title>

          <Row gutter={[24, 24]} justify="center">
            <Col xs={24} sm={12} md={6}>
              <div className="workflow-step" data-reveal data-delay="0">
                <div className="step-number">1</div>
                <h4>Customer pays — give them a code</h4>
                <p>Enter the amount in Tally. Get a unique code. Give it to your customer. Takes seconds.</p>
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div className="workflow-step" data-reveal data-delay="150">
                <div className="step-number">2</div>
                <h4>They enter the code — their visit is saved</h4>
                <p>They open go.gettally.store on any phone. Enter the code. Done. No download needed.</p>
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div className="workflow-step" data-reveal data-delay="300">
                <div className="step-number">3</div>
                <h4>They earn rewards — they keep coming back</h4>
                <p>Every visit earns them progress toward a free reward at your shop. That reward brings them back.</p>
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div className="workflow-step" data-reveal data-delay="450">
                <div className="step-number">4</div>
                <h4>Tally tells you when they stop — you win them back</h4>
                <p>Customer goes quiet? Tally tells you. You send one message. They come back. You sell more.</p>
              </div>
            </Col>
          </Row>
        </section>

        {/* ── ECOSYSTEM ───────────────────────────────────────────────── */}
        <section className="ecosystem-section">
          <Title level={2} className="ecosystem-title">
            Tally shows you who stopped coming. So you can bring them back.
          </Title>
          <div className="ecosystem-sub">
            Everything in Tally — the messages, the rewards, the AI, the alerts —
            exists for one reason: so more customers come back and you sell more.
          </div>

          <div className="tally-core-mockup" data-reveal data-delay="0">
            <div className="tcm-bar">
              <div className="tcm-dots"><span /><span /><span /></div>
              <div className="tcm-url">app.gettally.store</div>
            </div>
            <div className="tcm-body">
              <div className="tcm-greeting">Good morning, Adaeze 👋</div>
              <div className="tcm-summary">
                <div className="tcm-stat">
                  <div className="tcm-stat__val">₦47,500</div>
                  <div className="tcm-stat__lbl">Today's sales</div>
                </div>
                <div className="tcm-stat">
                  <div className="tcm-stat__val">214</div>
                  <div className="tcm-stat__lbl">Your customers</div>
                </div>
                <div className="tcm-stat tcm-stat--alert">
                  <div className="tcm-stat__val">8</div>
                  <div className="tcm-stat__lbl">⚠ Going quiet</div>
                </div>
              </div>

              <div className="tcm-ai-block">
                <div className="tcm-ai-block__label">✨ Tally noticed this</div>
                <p className="tcm-ai-block__text">
                  "Chioma comes every 12 days. She has not been here in 31 days.
                  She may be going somewhere else. Send her a message now?"
                </p>
                <div className="tcm-ai-block__actions">
                  <div className="tcm-ai-block__btn tcm-ai-block__btn--primary">Send message →</div>
                  <div className="tcm-ai-block__btn">See all 8</div>
                </div>
              </div>

              <div className="tcm-section-label">Customers going quiet — reach out now</div>
              <div className="tcm-customers">
                {[
                  { name: "Chioma A.", visits: 14, days: 31, avg: 12, urgency: "high" },
                  { name: "Bola O.",   visits: 9,  days: 44, avg: 18, urgency: "high" },
                  { name: "Emeka I.", visits: 7,  days: 22, avg: 14, urgency: "med"  },
                  { name: "Titi N.",  visits: 5,  days: 18, avg: 10, urgency: "med"  },
                ].map((c) => (
                  <div key={c.name} className={`tcm-cust tcm-cust--${c.urgency}`}>
                    <div className="tcm-cust__av">{c.name[0]}</div>
                    <div className="tcm-cust__info">
                      <div className="tcm-cust__name">{c.name}</div>
                      <div className="tcm-cust__detail">
                        {c.visits} visits · not seen in {c.days} days · usually comes every {c.avg} days
                      </div>
                    </div>
                    <div className="tcm-cust__action">Bring back</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <Row gutter={[32, 32]} className="ecosystem-grid">
            <Col xs={24} md={8}>
              <div className="ecosystem-item" data-reveal data-delay="0">
                <h4>You know every customer who ever bought from you</h4>
                <p>
                  Every time someone redeems a code, their name and details
                  are saved. You can see every customer — who came, when they
                  came, and how much they spent. No notebook. No guessing.
                </p>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div className="ecosystem-item" data-reveal data-delay="150">
                <h4>Tally tells you who to message — AI writes it for you</h4>
                <p>
                  When a customer goes quiet, Tally tells you and writes a
                  warm message to send them. You just approve and tap send.
                  They get the message. They come back. You sell more.
                </p>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div className="ecosystem-item" data-reveal data-delay="300">
                <h4>Rewards keep pulling customers back — on their own</h4>
                <p>
                  You set the reward. Tally tracks every visit. When a
                  customer is close to earning their reward, they feel the
                  pull to come in and complete it. You don't have to do
                  anything.
                </p>
              </div>
            </Col>
          </Row>

          <div className="ecosystem-cta-wrapper">
            <Button type="primary" size="large" className="ecosystem-cta">
              Start Using Tally for My Shop →
            </Button>
          </div>
        </section>

        {/* ── PRICING ─────────────────────────────────────────────────── */}
        <section className="pricing-section" id="pricing-plans">
          <Title level={2} className="section-title">
            Simple Pricing. No Surprises.
          </Title>

          <div className="pricing-grid">
            <div className="pricing-tier">
              <div className="tier-name">Free Trial</div>
              <div className="tier-price">₦0</div>
              <div className="tier-period">Your first 60 days — full access</div>
              <div className="tier-description">
                Everything included. No credit card. Start bringing customers
                back today.
              </div>
              <ul className="tier-features">
                <li>✓ Capture every customer who buys</li>
                <li>✓ Know when customers stop coming</li>
                <li>✓ Message all your customers — free</li>
                <li>✓ AI writes your win-back messages</li>
                <li>✓ Loyalty rewards that bring them back</li>
                <li>✓ Staff sale tracking</li>
              </ul>
              <Button type="primary" size="large" className="tier-cta-btn tier-cta-primary">
                Start Free — No Card Needed
              </Button>
            </div>

            <div className="pricing-tier pricing-tier--featured">
              <div className="tier-badge">Most popular</div>
              <div className="tier-name">Pro</div>
              <div className="tier-price">₦5,000</div>
              <div className="tier-period">per month after trial</div>
              <div className="tier-description">
                For shops serious about keeping every customer coming back.
              </div>
              <ul className="tier-features">
                <li>✓ Everything in Free Trial</li>
                <li>✓ Up to 3 staff accounts</li>
                <li>✓ Full sales reports</li>
                <li>✓ Customer VIP badges</li>
                <li>✓ Priority support</li>
              </ul>
              <Button type="primary" size="large" className="tier-cta-btn tier-cta-primary">
                Start Free, Upgrade Later
              </Button>
            </div>

            <div className="pricing-tier pricing-tier--anchor">
              <div className="tier-name">Enterprise</div>
              <div className="tier-price">₦39,000</div>
              <div className="tier-period">per month</div>
              <div className="tier-description">
                For bigger businesses with multiple branches who want
                customers coming back at every location.
              </div>
              <ul className="tier-features">
                <li>✓ Everything in Pro</li>
                <li>✓ Unlimited staff accounts</li>
                <li>✓ Multiple shop locations</li>
                <li>✓ Dedicated account manager</li>
                <li>✓ Custom onboarding</li>
              </ul>
              <Button type="default" size="large" className="tier-cta-btn tier-cta-outline">
                Contact Us
              </Button>
            </div>
          </div>

          <div className="pricing-reassurance">
            Cancel any time. Your customer data stays yours — always.
          </div>
        </section>

        {/* ── FAQ ─────────────────────────────────────────────────────── */}
        <section className="faq-section">
          <Title level={2} className="section-title">
            Common Questions
          </Title>

          <div className="faq-grid">
            <div className="faq-item" data-reveal data-delay="0">
              <h4 className="faq-question">
                Does my customer need to download an app?
              </h4>
              <p>
                No download, no app store. They open go.gettally.store on
                any phone browser, sign in, and enter their code. Done in
                under a minute. Works on any phone, even old ones.
              </p>
            </div>

            <div className="faq-item" data-reveal data-delay="100">
              <h4 className="faq-question">
                How does it stop my staff from hiding sales?
              </h4>
              <p>
                Every code is linked to the staff member who created it.
                If a code was given out but the customer never redeemed it,
                you see the gap in your dashboard — and you see exactly
                which staff member to ask about it.
              </p>
            </div>

            <div className="faq-item" data-reveal data-delay="0">
              <h4 className="faq-question">
                What if the customer does not redeem the code the same day?
              </h4>
              <p>
                No problem. Codes never expire. They can redeem it today,
                next week, or next month. The moment they do, they are in
                your customer list and you can message them any time.
              </p>
            </div>

            <div className="faq-item" data-reveal data-delay="100">
              <h4 className="faq-question">
                Why not just use WhatsApp?
              </h4>
              <p>
                With WhatsApp, you are paying to message your own customers.
                With Tally, it is always free. And Tally tells you which
                specific customers stopped coming — so you send the right
                message to the right person at the right time.
              </p>
            </div>

            <div className="faq-item" data-reveal data-delay="0">
              <h4 className="faq-question">
                How fast will I start seeing results?
              </h4>
              <p>
                Most shop owners see customers coming back within the first
                week. You sign up in minutes, record your first sale, and
                the moment a customer redeems their code, they are yours
                to bring back whenever you want.
              </p>
            </div>

            <div className="faq-item" data-reveal data-delay="100">
              <h4 className="faq-question">
                I have more than one shop. Can Tally work for all of them?
              </h4>
              <p>
                Yes. All your branches are on one screen. You see which
                customers are going quiet at each location and you can
                bring them back from one place.
              </p>
            </div>
          </div>
        </section>

        {/* ── FINAL CTA ───────────────────────────────────────────────── */}
        <section className="final-cta" data-reveal data-delay="0">
          <Title level={2} className="final-title">
            Tally helps you bring your customers back and sell more.
          </Title>
          <div className="final-supporting">
            <p>
              Right now, good customers are leaving your shop and you
              don't even know they are gone.
            </p>
            <p>
              Tally tells you who stopped coming. You send them a message.
              They come back. You sell more.
            </p>
            <p>
              Free for 60 days. No card. No hardware. No training needed.
            </p>
          </div>
          <Button type="primary" size="large" className="final-button">
            Start Bringing My Customers Back — It's Free
          </Button>
          <div className="final-sub-cta">
            No credit card · No hardware · Cancel any time
          </div>
        </section>

        {/* ── FOOTER ──────────────────────────────────────────────────── */}
        <footer className="tally-footer">
          <div className="footer-inner">
            <div className="footer-top">
              <div className="footer-brand">
                <div className="logo-group">
                  <img src={tallyIcon} alt="Tally" className="footer-icon" />
                  <div className="logo-text">Tally</div>
                </div>
                <div className="footer-tagline">
                  Tally helps you bring your customers back and sell more.
                  Free for your first 60 days.
                </div>
              </div>
              <div className="footer-nav">
                <div className="footer-nav-col">
                  <div className="footer-nav-label">Product</div>
                  <div className="links">
                    <a href="#how-it-works">How it works</a>
                    <a href="#pricing-plans">Pricing</a>
                    <a href="https://app.gettally.store">Merchant login</a>
                    <a href="https://go.gettally.store">Customer login</a>
                  </div>
                </div>
                <div className="footer-nav-col">
                  <div className="footer-nav-label">Company</div>
                  <div className="links">
                    <Link to="/about">About</Link>
                    <a href="mailto:hello@gettally.store">Contact</a>
                    <Link to="/privacy">Privacy policy</Link>
                  </div>
                </div>
                <div className="footer-nav-col">
                  <div className="footer-nav-label">Support</div>
                  <div className="links">
                    <Link to="/help">Help centre</Link>
                    <a href="https://wa.me/2348133413471" target="_blank" rel="noopener noreferrer">WhatsApp us</a>
                  </div>
                </div>
              </div>
            </div>
            <div className="footer-bottom">
              <div className="copyright">© 2026 Tally. Made for Nigerian shop owners.</div>
              <div className="footer-badge">Free for your first 60 days</div>
            </div>
          </div>
        </footer>

      </div>
    </ConfigProvider>
  );
};

export default LandingPreview;